// 遠端協助對話框（SPEC #kinio-kms-remote「操作者流程」、KMS-10 / KMS-29 畫面；k1-wbs M4-06）：
//   發起（選權限、理由；主控台使用者不是管理員時黃條）→ passkey 確認（step-up 收據）→ 叫起遠端工具 →
//   等待〈姓名〉允許（0:58；沒偵測到遠端工具就引導下載，裝好回來自動再開一次）→ 已連上（加權限、延長、結束；加權限等待中）→
//   結果與下一步（拒絕 / 沒回應 / 沒人登入 / 鎖定 / 回報…）或結束畫面（時長、權限、檔案、加一句備註寫進紀錄）。
//   螢幕鎖定（M4-06b）：Agent 留著這次請求，對方解鎖就自己跳允許視窗 → 這裡等著，接續後再拿一次遠端工具的網址（…/launch）；
//   對方電腦有多個登入的工作階段時寫明允許視窗送到螢幕前（主控台）的〈姓名〉。
// 〈姓名〉一律是主要使用者，沒設時寫「螢幕前的人」，不顯示本機帳號。畫面不出現 relay / ICE 等字。
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '../../../components/Dialog';
import { useApp } from '../../../lib/AppContext';
import { useMarketAvailability } from '../../../lib/marketGate';
import { kmgrRemoteReceipt, reauthWithPasskey } from '../../../lib/session';
import { timeStamp } from '../../../lib/timefmt';
import { errText, NS } from '../parts';
import { remoteApi } from './api';
import {
  addablePerms,
  assistReason,
  canExtend,
  clock,
  consentSince,
  CONSENT_SECONDS,
  DEFAULT_PERMS,
  durationSeconds,
  durationText,
  endOutcomeOf,
  errorOutcome,
  EXTEND_MINUTES,
  filesOf,
  launched,
  LAUNCH_CODE_MS,
  LAUNCH_DETECT_MS,
  otherSessions,
  pendingUpgrade,
  permsList,
  permsText,
  relaySlow,
  secondsLeft,
  sizeText,
  togglePerm,
  waitUnlockOf,
  type Outcome,
} from './model';
import { REMOTE_PERMS, type AssistRequest, type RemoteEvent, type RemotePerm, type RemoteSession } from './types';

/** 要協助的那台電腦（主機頁或協助請求帶進來）。 */
export interface RemoteTarget {
  home_id: string;
  device_ref: string;
  api: string;
  hostname?: string;
  /** 主要使用者（〈姓名〉）；沒設 = 「螢幕前的人」 */
  primary_user?: string;
  console_user_admin?: boolean;
  home_name?: string;
  org_name?: string;
}

const TOOL_SEEN_KEY = 'vms.kmgr.remote.tool_seen';

function toolSeen(): boolean {
  try {
    return localStorage.getItem(TOOL_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markToolSeen(): void {
  try {
    localStorage.setItem(TOOL_SEEN_KEY, '1');
  } catch {
    /* 私密視窗：下次再提示一次下載 */
  }
}

/** 叫起遠端工具（kinio-remote://）：同一頁 assign，沒裝時瀏覽器什麼都不做，頁面不會離開。 */
export function openRemoteTool(url: string): void {
  if (!url.startsWith('kinio-remote://')) return;
  try {
    window.location.assign(url);
  } catch {
    /* 瀏覽器擋外部協定：畫面上有〔開啟遠端工具〕可以按 */
  }
}

function useNow(on: boolean, ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [on, ms]);
  return now;
}

type Phase = 'form' | 'auth' | 'live' | 'result';

export function RemoteDialog({ target, assist, resumeSessionId, onClose }: { target: RemoteTarget; assist?: AssistRequest; resumeSessionId?: string; onClose(): void }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const name = target.primary_user?.trim() || t('remote.screen_person');
  const host = target.hostname || t('target.host');
  const [perms, setPerms] = useState<RemotePerm[]>(DEFAULT_PERMS);
  const [reason, setReason] = useState(() => (assist ? assistReason(t, assist) : ''));
  const [phase, setPhase] = useState<Phase>(resumeSessionId ? 'live' : 'form');
  const [sid, setSid] = useState<string | undefined>(resumeSessionId);
  const [launch, setLaunch] = useState<{ url: string; at: number } | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [toolReady, setToolReady] = useState(toolSeen());

  const cleanup = (id: string) => void remoteApi.end(target.api, target.home_id, target.device_ref, id).catch(() => undefined);

  const submit = async () => {
    setFormErr(null);
    setOutcome(null);
    setPhase('auth');
    let created: { session_id: string; ticket_hash: string };
    try {
      created = await remoteApi.create(target.api, target.home_id, target.device_ref, {
        mode: 'attended',
        permissions: perms,
        reason: reason.trim() || undefined,
        assist_request_id: assist?.request_id,
      });
    } catch (e) {
      setOutcome(errorOutcome(e));
      setPhase('result');
      return;
    }
    setSid(created.session_id);
    try {
      const receipt = await kmgrRemoteReceipt(created.ticket_hash);
      const st = await remoteApi.start(target.api, target.home_id, target.device_ref, created.session_id, receipt);
      setLaunch({ url: st.launch_url, at: Date.now() });
      openRemoteTool(st.launch_url);
      setPhase('live');
    } catch (e) {
      // 螢幕鎖定、Agent 留著請求等解鎖：不收掉 session，進「等對方解鎖」（解鎖後接續、再拿遠端工具的網址）
      if (waitUnlockOf(e)) {
        setPhase('live');
        void qc.invalidateQueries({ queryKey: ['kmgr', 'remote'] });
        return;
      }
      // 沒開始的 session 收掉（不然這台 5 分鐘內都是「已有進行中的協助」）；Agent 拒絕的 kmgr 已標結束，再送只是 no-op
      cleanup(created.session_id);
      const o = errorOutcome(e);
      if (o.back) {
        setSid(undefined);
        setFormErr(t('remote.passkey_canceled'));
        setPhase('form');
        return;
      }
      setOutcome(o);
      setPhase('result');
    } finally {
      void qc.invalidateQueries({ queryKey: ['kmgr', 'remote'] });
    }
  };

  const retry = () => {
    setSid(undefined);
    setLaunch(null);
    void submit();
  };

  const title = t('remote.dialog_title', { host });
  return (
    <Dialog open title={title} onClose={onClose} wide testId="km-remote">
      <div className="km km-remote" data-phase={phase}>
        {phase === 'form' && (
          <RemoteForm
            target={target}
            name={name}
            perms={perms}
            setPerms={setPerms}
            reason={reason}
            setReason={setReason}
            assist={assist}
            err={formErr}
            toolReady={toolReady}
            onToolReady={() => setToolReady(true)}
            onCancel={onClose}
            onSubmit={() => void submit()}
          />
        )}
        {phase === 'auth' && (
          <div className="km-remote-wait" role="status" data-testid="km-remote-auth">
            <div className="km-spinner" aria-hidden />
            <p>{t('remote.auth_waiting')}</p>
          </div>
        )}
        {phase === 'result' && outcome && <OutcomeView o={outcome} name={name} host={host} target={target} sessionId={sid} onRetry={retry} onClose={onClose} />}
        {phase === 'live' && sid && (
          <LiveSession
            target={target}
            name={name}
            host={host}
            sid={sid}
            launch={launch}
            onRelaunch={(url) => setLaunch({ url, at: launch?.at ?? Date.now() })}
            onLaunch={(url) => setLaunch({ url, at: Date.now() })}
            onRetry={retry}
            onClose={onClose}
          />
        )}
      </div>
    </Dialog>
  );
}

// ---- 發起 ----

function RemoteForm(p: {
  target: RemoteTarget;
  name: string;
  perms: RemotePerm[];
  setPerms(v: RemotePerm[]): void;
  reason: string;
  setReason(v: string): void;
  assist?: AssistRequest;
  err: string | null;
  toolReady: boolean;
  onToolReady(): void;
  onCancel(): void;
  onSubmit(): void;
}) {
  const { t } = useTranslation(NS);
  const { target, name, perms } = p;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        p.onSubmit();
      }}
      data-testid="km-remote-form"
    >
      <p className="km-remote-lead">
        {t('remote.form_lead', {
          name,
          org: target.org_name || t('remote.your_org'),
        })}
      </p>
      {target.console_user_admin === false && (
        <div className="km-bar warn" role="note" data-testid="km-remote-not-admin">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">{t('remote.not_admin', { name })}</span>
        </div>
      )}
      <fieldset className="km-remote-perms">
        <legend>{t('remote.perms_title')}</legend>
        {REMOTE_PERMS.map((x) => (
          <label key={x} className="km-check" data-perm={x}>
            <input type="checkbox" checked={perms.includes(x)} disabled={x === 'view'} onChange={(e) => p.setPerms(togglePerm(perms, x, e.target.checked))} />
            <span>
              {t(`remote.perm.${x}`)}
              <span className="muted km-sub"> · {t(`remote.perm_hint.${x}`)}</span>
            </span>
          </label>
        ))}
        <p className="muted km-sub" style={{ margin: '4px 0 0' }}>
          {t('remote.no_recording')}
        </p>
      </fieldset>
      <label className="field">
        <span>{t('remote.reason')}</span>
        <textarea className="input" rows={2} maxLength={500} value={p.reason} onChange={(e) => p.setReason(e.target.value)} placeholder={t('remote.reason_ph')} />
      </label>
      {!p.toolReady && <ToolDownloads first onReady={p.onToolReady} />}
      <p className="muted km-sub">{t('remote.form_note')}</p>
      {p.err && (
        <p className="error small" role="alert">
          {p.err}
        </p>
      )}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={p.onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary" data-testid="km-remote-start">
          {t('remote.start_btn')}
        </button>
      </div>
    </form>
  );
}

/** 遠端工具下載（Windows / macOS / Linux）。first = 發起前（這個瀏覽器還沒看過遠端工具開起來）。 */
function ToolDownloads({ first, onReady, relaunchUrl }: { first?: boolean; onReady?(): void; relaunchUrl?: string }) {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const dl = config.kmgrctl_downloads ?? {};
  // 歐盟 / 英國市場閘門（SPEC #product-security-market-gate）：連線地區不提供時不列下載連結
  const market = useMarketAvailability(config, 'kmgrctl');
  const plats = market.available ? (['windows', 'macos', 'linux'] as const).filter((k) => dl[k]) : [];
  return (
    <div className="km-bar unk km-remote-tool" data-testid="km-remote-tool">
      <span className="ic" aria-hidden>
        ⇩
      </span>
      <div className="txt">
        <strong>{first ? t('remote.tool_first') : t('remote.tool_missing')}</strong>
        <div className="km-sub">{first ? t('remote.tool_first_body') : t('remote.tool_missing_body')}</div>
        <div className="km-remote-dl">
          {!market.available ? (
            <span className="muted km-sub" role="status" data-testid="market-gate-notice" data-product="kmgrctl">
              <strong>{t('market.title')}</strong> {t('market.tool_body')}
            </span>
          ) : plats.length ? (
            plats.map((k) => (
              <a key={k} className="btn btn-sm" href={dl[k]} rel="noopener" data-dl={k}>
                {t(`remote.dl.${k}`)}
              </a>
            ))
          ) : (
            <span className="muted km-sub" data-testid="km-remote-dl-none">
              {t('remote.dl_none')}
            </span>
          )}
          {relaunchUrl && (
            <a className="btn btn-sm btn-primary" href={relaunchUrl} data-testid="km-remote-relaunch">
              {t('remote.relaunch')}
            </a>
          )}
          {first && onReady && (
            <button type="button" className="btn btn-sm" onClick={onReady} data-testid="km-remote-tool-ready">
              {t('remote.tool_installed')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- 結果與下一步（還沒連上就結束的） ----

function OutcomeView({ o, name, host, target, sessionId, onRetry, onClose }: { o: Outcome; name: string; host: string; target: RemoteTarget; sessionId?: string; onRetry(): void; onClose(): void }) {
  const { t } = useTranslation(NS);
  const [noting, setNoting] = useState(false);
  const vars = { name, host, org: target.org_name || t('remote.your_org') };
  const nextText = t(`remote.out_next.${o.key}`, { ...vars, defaultValue: '' });
  return (
    <div className="km-remote-result" data-testid="km-remote-result" data-outcome={o.key} role="status">
      <h3 className="km-remote-h">
        <span aria-hidden className={'km-remote-ic ' + (o.key === 'reported' || o.key === 'denied' ? 'bad' : 'warn')}>
          {o.key === 'denied' || o.key === 'reported' ? '✕' : '!'}
        </span>{' '}
        {t(`remote.out.${o.key}`, vars)}
      </h3>
      {nextText && <p>{nextText}</p>}
      {o.code && <p className="muted km-sub">{t('remote.err_code', { code: o.code })}</p>}
      {noting && sessionId ? (
        <NoteForm target={target} sessionId={sessionId} onDone={onClose} doneLabel={t('remote.note_save_close')} skipLabel={t('remote.done_no_note')} />
      ) : (
        <div className="dialog-actions">
          <button className="btn" onClick={onClose}>
            {t('remote.close')}
          </button>
          {o.next === 'note' && sessionId && (
            <button className="btn btn-primary" onClick={() => setNoting(true)} data-testid="km-remote-note-open">
              {t('remote.note_then_close')}
            </button>
          )}
          {o.next === 'retry' && (
            <button className="btn btn-primary" onClick={onRetry} data-testid="km-remote-retry">
              {t('remote.retry')}
            </button>
          )}
          {o.next === 'passkey' && (
            <Link className="btn btn-primary" to="/settings?tab=security">
              {t('remote.go_passkey')}
            </Link>
          )}
          {o.next === 'policy' && (
            <Link className="btn btn-primary" to="/org/kmgr">
              {t('remote.go_policy')}
            </Link>
          )}
          {o.next === 'seats' && (
            <Link className="btn btn-primary" to="/kmgr/hosts?tab=unlicensed">
              {t('remote.go_seats')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function NoteForm({ target, sessionId, onDone, doneLabel, skipLabel }: { target: RemoteTarget; sessionId: string; onDone(): void; doneLabel: string; skipLabel?: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [ticket, setTicket] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await remoteApi.end(target.api, target.home_id, target.device_ref, sessionId, {
        note: note.trim() || undefined,
        external_ticket: ticket.trim() || undefined,
      });
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['kmgr', 'remote'] });
      onDone();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="km-remote-note" data-testid="km-remote-note">
      <label className="field">
        <span>{t('remote.note')}</span>
        <textarea className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('remote.note_ph')} />
      </label>
      <label className="field">
        <span>{t('remote.external_ticket')}</span>
        <input className="input" maxLength={128} value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder={t('remote.external_ticket_ph')} />
      </label>
      <p className="muted km-sub">{t('remote.note_audit')}</p>
      {err && (
        <p className="error small" role="alert">
          {err}
        </p>
      )}
      {saved && <p className="km-sub">{t('remote.note_saved')}</p>}
      <div className="dialog-actions">
        {skipLabel && (
          <button className="btn" onClick={onDone} data-testid="km-remote-note-skip">
            {skipLabel}
          </button>
        )}
        <button className="btn btn-primary" disabled={busy || (!note.trim() && !ticket.trim())} onClick={() => void save()} data-testid="km-remote-note-save">
          {doneLabel}
        </button>
      </div>
    </div>
  );
}

// ---- 進行中：等待允許 → 已連上 → 結束 ----

function LiveSession({
  target,
  name,
  host,
  sid,
  launch,
  onRelaunch,
  onLaunch,
  onRetry,
  onClose,
}: {
  target: RemoteTarget;
  name: string;
  host: string;
  sid: string;
  launch: { url: string; at: number } | null;
  onRelaunch(url: string): void;
  /** 新拿到的遠端工具網址（…/launch；解鎖後接續、或回到協助時再開） */
  onLaunch(url: string): void;
  onRetry(): void;
  onClose(): void;
}) {
  const { t } = useTranslation(NS);
  const q = useQuery({
    queryKey: ['kmgr', 'remote', 'session', sid],
    queryFn: () => remoteApi.get(target.api, target.home_id, target.device_ref, sid),
    refetchInterval: (qq: { state: { data?: { session: RemoteSession } } }) => (qq.state.data?.session.state === 'ended' ? false : 2000),
  });
  const s = q.data?.session;
  const events = q.data?.events;
  const isLaunched = launched(events);
  useEffect(() => {
    if (isLaunched) markToolSeen();
  }, [isLaunched]);
  // 解鎖後 Agent 自己跳了允許視窗：自動拿一次遠端工具的網址並叫起（只一次）
  const unlocked = !!events?.some((e) => e.kind === 'unlocked');
  const autoLaunched = useRef(false);
  useEffect(() => {
    if (!s || s.state !== 'awaiting_consent' || !unlocked || launch || isLaunched || autoLaunched.current) return;
    autoLaunched.current = true;
    void remoteApi
      .relaunch(target.api, target.home_id, target.device_ref, s.session_id)
      .then((r) => {
        onLaunch(r.launch_url);
        openRemoteTool(r.launch_url);
      })
      .catch(() => undefined);
  }, [s, unlocked, launch, isLaunched, target, onLaunch]);
  if (!s)
    return (
      <div className="km-remote-wait" role="status">
        {q.isError ? <p className="error small">{errText(t, q.error)}</p> : <div className="km-spinner" aria-hidden />}
      </div>
    );
  if (s.state === 'requested' && s.wait_unlock_until) return <WaitingUnlock s={s} name={name} target={target} />;
  if (s.state === 'requested' || s.state === 'awaiting_consent')
    return <Waiting s={s} events={events} name={name} target={target} launch={launch} launchedNow={isLaunched} onRelaunch={onRelaunch} onLaunch={onLaunch} />;
  if (s.state === 'active' || s.state === 'upgrade_pending') return <Active s={s} events={events} name={name} host={host} target={target} />;
  const o = endOutcomeOf(s, events);
  if (o) return <OutcomeView o={o} name={name} host={host} target={target} sessionId={sid} onRetry={onRetry} onClose={onClose} />;
  return <Ended s={s} events={events} name={name} host={host} target={target} onClose={onClose} />;
}

/** 螢幕鎖定：Agent 留著請求，對方解鎖就自己跳允許視窗（kmgr wait_unlock_until；M4-06b）。 */
function WaitingUnlock({ s, name, target }: { s: RemoteSession; name: string; target: RemoteTarget }) {
  const { t } = useTranslation(NS);
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await remoteApi.end(target.api, target.home_id, target.device_ref, s.session_id);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="km-remote-waiting" data-testid="km-remote-wait-unlock">
      <div className="km-remote-wait" role="status" aria-live="polite">
        <div className="km-spinner" aria-hidden />
        <h3 className="km-remote-h">{t('remote.wait_unlock', { name })}</h3>
        <p className="muted">{t('remote.wait_unlock_body', { name, time: timeStamp(s.wait_unlock_until ?? s.created_at, undefined, false) })}</p>
      </div>
      <div className="dialog-actions">
        <button className="btn" disabled={busy} onClick={() => void cancel()} data-testid="km-remote-cancel">
          {t('remote.cancel_session')}
        </button>
      </div>
    </div>
  );
}

function Waiting({
  s,
  events,
  name,
  target,
  launch,
  launchedNow,
  onRelaunch,
  onLaunch,
}: {
  s: RemoteSession;
  events?: RemoteEvent[];
  name: string;
  target: RemoteTarget;
  launch: { url: string; at: number } | null;
  launchedNow: boolean;
  onRelaunch(url: string): void;
  onLaunch(url: string): void;
}) {
  const { t } = useTranslation(NS);
  const now = useNow(true);
  const [busy, setBusy] = useState(false);
  const left = secondsLeft(consentSince(s, events), CONSENT_SECONDS, now);
  const missing = !!launch && !launchedNow && now - launch.at > LAUNCH_DETECT_MS;
  // 裝好遠端工具回到這一頁（視窗重新取得焦點）：launch code 還在效期就自動再開一次（只一次）
  const relaunched = useRef(false);
  useEffect(() => {
    if (!missing || !launch) return;
    const back = () => {
      if (relaunched.current || document.visibilityState === 'hidden' || Date.now() - launch.at > LAUNCH_CODE_MS - 5_000) return;
      relaunched.current = true;
      openRemoteTool(launch.url);
      onRelaunch(launch.url);
    };
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', back);
    return () => {
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', back);
    };
  }, [missing, launch, onRelaunch]);
  const cancel = async () => {
    setBusy(true);
    try {
      await remoteApi.end(target.api, target.home_id, target.device_ref, s.session_id);
    } finally {
      setBusy(false);
    }
  };
  // 沒有網址（回到協助、或解鎖後自動拿失敗）：按了才向 kmgr 拿一個新的再叫起
  const openTool = async () => {
    setBusy(true);
    try {
      const r = await remoteApi.relaunch(target.api, target.home_id, target.device_ref, s.session_id);
      onLaunch(r.launch_url);
      openRemoteTool(r.launch_url);
    } catch {
      /* 已兌換過 / 已結束：畫面跟著 session 狀態走 */
    } finally {
      setBusy(false);
    }
  };
  const others = otherSessions(events);
  return (
    <div className="km-remote-waiting" data-testid="km-remote-waiting" data-launch-url={launch?.url} data-tool={launchedNow ? 'open' : missing ? 'missing' : 'checking'}>
      <div className="km-remote-wait" role="status" aria-live="polite">
        <div className="km-spinner" aria-hidden />
        <h3 className="km-remote-h" data-testid="km-remote-countdown">
          {left > 0 ? t('remote.waiting', { name, time: clock(left) }) : t('remote.waiting_over', { name })}
        </h3>
        <p className="muted">{t('remote.waiting_body', { name })}</p>
        {others > 0 && (
          <p className="km-sub" data-testid="km-remote-sent-to">
            {t('remote.sent_to_console', { name, n: others })}
          </p>
        )}
      </div>
      {launchedNow ? (
        <p className="km-sub km-remote-ok" data-testid="km-remote-tool-open">
          <span aria-hidden>✓ </span>
          {t('remote.tool_open')}
        </p>
      ) : missing ? (
        <ToolDownloads relaunchUrl={launch?.url} />
      ) : launch ? (
        <p className="km-sub muted">
          {t('remote.tool_opening')}{' '}
          <a href={launch.url} data-testid="km-remote-open-tool">
            {t('remote.relaunch')}
          </a>
        </p>
      ) : (
        s.state === 'awaiting_consent' && (
          <p className="km-sub muted">
            <button className="btn btn-sm" disabled={busy} onClick={() => void openTool()} data-testid="km-remote-get-tool">
              {t('remote.relaunch')}
            </button>
          </p>
        )
      )}
      <div className="dialog-actions">
        <button className="btn" disabled={busy} onClick={() => void cancel()} data-testid="km-remote-cancel">
          {t('remote.cancel_session')}
        </button>
      </div>
    </div>
  );
}

function Active({ s, events, name, host, target }: { s: RemoteSession; events?: RemoteEvent[]; name: string; host: string; target: RemoteTarget }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const now = useNow(true);
  const pending = pendingUpgrade(s, events);
  const [adding, setAdding] = useState(false);
  const [add, setAdd] = useState<RemotePerm[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const endsMs = Date.parse(s.ends_at);
  const minLeft = Math.max(0, Math.ceil((endsMs - now) / 60_000));
  const addable = addablePerms(s.permissions);
  const upgrade = async (body: { add_permissions?: RemotePerm[]; extend?: boolean }, key: string) => {
    setBusy(key);
    setErr(null);
    try {
      // 加權限 / 延長要 5 分鐘內的 passkey step-up（密碼不算）
      await reauthWithPasskey();
      await remoteApi.upgrade(target.api, target.home_id, target.device_ref, s.session_id, body);
      setAdding(false);
      setAdd([]);
      void qc.invalidateQueries({
        queryKey: ['kmgr', 'remote', 'session', s.session_id],
      });
    } catch (e) {
      const o = errorOutcome(e);
      setErr(o.back ? t('remote.passkey_canceled') : errText(t, e));
    } finally {
      setBusy(null);
    }
  };
  const end = async () => {
    setBusy('end');
    setErr(null);
    try {
      await remoteApi.end(target.api, target.home_id, target.device_ref, s.session_id);
      void qc.invalidateQueries({ queryKey: ['kmgr', 'remote'] });
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="km-remote-active" data-testid="km-remote-active" data-state={s.state}>
      <h3 className="km-remote-h">
        <span aria-hidden className="km-remote-ic ok">
          ●
        </span>{' '}
        {t('remote.connected', { host })}
        {relaySlow(s.relay_kind) && (
          <span className="km-remote-slow" data-testid="km-remote-slow">
            {t('remote.slow')}
          </span>
        )}
      </h3>
      <p className="muted">{t('remote.connected_body', { name })}</p>
      {pending && (
        <div className="km-bar warn" role="status" data-testid="km-remote-upgrade-pending">
          <span className="ic" aria-hidden>
            ⏳
          </span>
          <span className="txt">
            {t('remote.upgrade_waiting', {
              name,
              what: [pending.add.length ? permsList(t, pending.add) : '', pending.extend ? t('remote.extend_what', { n: EXTEND_MINUTES }) : ''].filter(Boolean).join(t('remote.list_sep')),
              time: clock(secondsLeft(pending.sinceMs, CONSENT_SECONDS, now)),
            })}
            <span className="km-sub muted"> {t('remote.upgrade_input_off')}</span>
          </span>
        </div>
      )}
      <dl className="km-dl">
        <dt>{t('remote.col_perms')}</dt>
        <dd data-testid="km-remote-perms">{permsText(t, s.permissions)}</dd>
        <dt>{t('remote.col_until')}</dt>
        <dd className={minLeft <= 5 ? 'km-metric attention' : undefined}>
          {t('remote.until', {
            time: timeStamp(s.ends_at, undefined, false),
            n: minLeft,
          })}
        </dd>
        <dt>{t('remote.col_recording')}</dt>
        <dd>{t('remote.no_recording_short')}</dd>
      </dl>
      <ul className="km-remote-tips muted km-sub">
        <li>{t('remote.tip_uac')}</li>
        <li>{t('remote.tip_cad')}</li>
      </ul>
      {adding && (
        <fieldset className="km-remote-perms" data-testid="km-remote-add">
          <legend>{t('remote.add_title')}</legend>
          {addable.map((x) => (
            <label key={x} className="km-check" data-perm={x}>
              <input type="checkbox" checked={add.includes(x)} onChange={(e) => setAdd(e.target.checked ? [...add, x] : add.filter((y) => y !== x))} />
              <span>{t(`remote.perm.${x}`)}</span>
            </label>
          ))}
          <p className="muted km-sub">{t('remote.add_note', { name })}</p>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={!add.length || !!busy} onClick={() => void upgrade({ add_permissions: add }, 'add')} data-testid="km-remote-add-send">
              {t('remote.add_send')}
            </button>
          </div>
        </fieldset>
      )}
      {err && (
        <p className="error small" role="alert">
          {err}
        </p>
      )}
      {!adding && (
        <div className="dialog-actions">
          {addable.length > 0 && (
            <button className="btn" disabled={!!busy || !!pending} onClick={() => setAdding(true)} data-testid="km-remote-add-open">
              {t('remote.add_btn')}
            </button>
          )}
          {canExtend(s) && (
            <button className="btn" disabled={!!busy || !!pending} onClick={() => void upgrade({ extend: true }, 'extend')} data-testid="km-remote-extend">
              {t('remote.extend_btn', { n: EXTEND_MINUTES })}
            </button>
          )}
          <button className="btn btn-danger" disabled={!!busy} onClick={() => void end()} data-testid="km-remote-end">
            {t('remote.end_btn')}
          </button>
        </div>
      )}
    </div>
  );
}

function Ended({ s, events, name, host, target, onClose }: { s: RemoteSession; events?: RemoteEvent[]; name: string; host: string; target: RemoteTarget; onClose(): void }) {
  const { t } = useTranslation(NS);
  const files = filesOf(events);
  const dur = durationSeconds(s, events);
  return (
    <div className="km-remote-ended" data-testid="km-remote-ended" data-reason={s.end_reason}>
      <h3 className="km-remote-h">
        <span aria-hidden className="km-remote-ic">
          ■
        </span>{' '}
        {t('remote.ended_title', { host })}
      </h3>
      <p>
        {t(`remote.ended_by.${s.end_reason}`, {
          name,
          defaultValue: t('remote.ended_by.operator_end'),
        })}
      </p>
      <dl className="km-dl">
        <dt>{t('remote.col_duration')}</dt>
        <dd data-testid="km-remote-duration">{durationText(t, dur)}</dd>
        <dt>{t('remote.col_perms')}</dt>
        <dd>{permsText(t, s.permissions)}</dd>
        <dt>{t('remote.col_files')}</dt>
        <dd data-testid="km-remote-files">
          {files.length ? (
            <ul className="km-remote-files">
              {files.map((f, i) => (
                <li key={i}>
                  {f.direction === 'from_host' ? '↑ ' : '↓ '}
                  {f.name} <span className="muted">· {sizeText(f.size)}</span>
                </li>
              ))}
            </ul>
          ) : (
            t('remote.no_files')
          )}
        </dd>
        <dt>{t('remote.col_recording')}</dt>
        <dd>{t('remote.no_recording_short')}</dd>
      </dl>
      {s.note || s.external_ticket ? (
        <>
          <p className="km-sub" data-testid="km-remote-note-shown">
            {t('remote.note_shown', {
              note: s.note ?? '—',
              ticket: s.external_ticket ?? '—',
            })}
          </p>
          <div className="dialog-actions">
            <button className="btn btn-primary" onClick={onClose}>
              {t('remote.done')}
            </button>
          </div>
        </>
      ) : (
        <>
          <NoteForm target={target} sessionId={s.session_id} onDone={onClose} doneLabel={t('remote.note_save_close')} skipLabel={t('remote.done_no_note')} />
        </>
      )}
    </div>
  );
}
